import React, {
  useEffect,
  forwardRef,
  useCallback,
  useContext,
  useRef,
} from "react";
import {BryntumSchedulerPro} from "@bryntum/schedulerpro-react";
import { DateHelper } from "@bryntum/schedulerpro";
import {SCHEDULER_CONFIG} from "../../other/constants/SchedulerConfig.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import {EDITOR_KEYS} from "../../other/constants/EditorKeys.jsx";
import { canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import SwivuSchedulerMenuPlugin from "../../bryntum-lib/plugins/SwivuSchedulerMenuPlugin.jsx";
import SwivuSchedulerEventMenuPlugin from "../../bryntum-lib/plugins/SwivuSchedulerEventMenuPlugin.jsx";
import {processCellMenuItems} from "../../other/utils/gridCellMenuUtils.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import ResourceChecklistTab from "../../bryntum-lib/editors/ResourceEditorV2/ResourceChecklistTab.jsx";
import ResourceRemindersTab from "../../bryntum-lib/editors/ResourceEditorV2/ResourceRemindersTab.jsx";
import ResourceContactTab from "../../bryntum-lib/editors/ResourceEditorV2/ResourceContactTab.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { getOriginalRecord, openNewSpotIncentiveEditor } from "../../other/utils/utils.jsx";
import useViews from "../hooks/useViews.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

const EDITOR_TABS = {
  [WIDGET_KEYS.checklistColumn]: ResourceChecklistTab.type,
  [WIDGET_KEYS.alertBellColumn]: ResourceRemindersTab.type,
  [WIDGET_KEYS.contactColumn]: ResourceContactTab.type,
}

const BryntumSchedulerWrapper = forwardRef(({ onTaskFind, ...props }, ref) => {
  const viewManagerState  = useViews();
  const schedulerRef = useRef(null);
  const context = useContext(BryntumContext);
  const {editorHandler, project, user, currentViewRef, entityManager} = context;

  useEffect(() => {
    if (schedulerRef.current) {
      schedulerRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Resource",
        editorHandler,
        grid: schedulerRef.current.instance,
        user,
        promptOnRemove: true,
        project,
        tagStoreKey: STORE_KEYS.resourceTagValuesStore,
        onEditEntity(record) {
          const originalRecord = getOriginalRecord(record);
          if (user.can(GRANTS.grant_four)) {
            editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, originalRecord, { resource: originalRecord });
          }
        }
      });
    }

    return () => {
      currentViewRef.current.swivuLastViewportCenterDate = null;
    }
  }, [editorHandler, user]);

  useEffect(() => {
    return () => {
      // Bug:
      // When timespans are highlighted and scheduler view state is switch (e.g: Default view to Custom view).
      // It causes a internal crash in scheduler therefore un-highlighting records when switching state
      if (schedulerRef.current) {
        schedulerRef.current.instance.features.timeSpanHighlight.unhighlightTimeSpans();
      }
    }
  }, [viewManagerState]);

  const onCreateNewTask = useCallback(({date, resourceRecord}) => {
    const timeAxis = schedulerRef.current.instance.timeAxis;
    const { startDate, endDate } = timeAxis.getAt(Math.floor(timeAxis.getTickFromDate(date)));
    const newRecord = project.extraTaskStore.createRecord();
    const resource = getOriginalRecord(resourceRecord);
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
      task: newRecord,
      onAfterRecordAdd: (taskRecord) => {
        const [newEvent] = project.eventStore.add({
          startDate,
          endDate,
          taskId: taskRecord.id
        });
        project.assignmentStore.add({
          resource,
          event: newEvent
        });
      }
    });
  }, [editorHandler, project]);

  const onCreateNewTaskEventByDragCreate = useCallback(({context, resourceRecord}) => {
    context.async = true;
    // by default, bryntum tries to make a sync call for record creation but we should prevent it as bryntum record creation doesn't include task record
    context.finalize(false);
    const {startDate, endDate} = context;
    const resource = getOriginalRecord(resourceRecord);
    const newRecord = project.extraTaskStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
      task: newRecord,
      onAfterRecordAdd: (taskRecord) => {
        const [newEvent] = project.eventStore.add({
          startDate,
          endDate,
          taskId: taskRecord.id
        });
        project.assignmentStore.add({
          resource,
          event: newEvent
        });
      }
    });
  }, [editorHandler, project]);

  const onCreateNewTaskReplaceEvent = useCallback(({date, resourceRecord, eventRecord}) => {
    const startDate = eventRecord.startDate || date;
    const endDate = eventRecord.endDate || DateHelper.add(startDate, 1, 'day');
    const newRecord = project.extraTaskStore.createRecord();
    const resource = getOriginalRecord(resourceRecord)
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
      task: newRecord,
      resource,
      removeEventAfterAdd: {eventRecord},
      onAfterRecordAdd: (taskRecord) => {
        eventRecord.remove();
        const [newEvent] = project.eventStore.add({
          startDate,
          endDate,
          taskId: taskRecord.id
        });
        project.assignmentStore.add({
          resource,
          event: newEvent
        });
      }
    });
  }, [editorHandler, project]);

  const addReminderOnTask = ({eventRecord}) => {
    const taskRecord = getOriginalRecord(eventRecord.task);
    entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id, otherParams: { addReminder: true } });
  };

  const showResourcePopupEditor = useCallback(({ record, column }) => {
    const originalRecord = getOriginalRecord(record);
    if (column.type !== "timeAxis" && canOpenEditor(column, record)) {
      editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, originalRecord, {
        resource: originalRecord, selectTabByTypeOnOpen: EDITOR_TABS[column.type] || null
      });
    }
  }, [editorHandler]);

  const onGridCellClick = debounce(({ column, record, event }) => {
    const originalRecord = getOriginalRecord(record);

    if (canOpenEditor(column, originalRecord) && event.detail === 1 && (column.isTagColumnBase || column.isTeamColumn || column.isAssignToColumn || column.isEligibleUserColumn || column.type === WIDGET_KEYS.columnResourceType)) {
        editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: originalRecord,
          event,
          column
        }, { by: "recordType", useExisting: true });
    }
  }, 300)


  const onSwivuBeforePaste = useCallback(({ eventRecords }) => {
    const [eventRecord] = eventRecords;
    if (!eventRecord.taskId) {
      console.warn("Pasted event record does not have task id", eventRecords);
      return false;
    }
  }, []);

  const onSwivuEventPaste = (({ eventRecords, date, source }) => {
    const [eventRecord] = eventRecords;
    const selectedTick = source.timeAxis.getAt(Math.floor(source.timeAxis.getTickFromDate(date)));

    // Align start date to tick while preserving the original event duration
    eventRecord.startDate = DateHelper.clone(selectedTick.startDate);
  })

  return (
    <>
      <BryntumSchedulerPro
        ref={(schRef) => {
          ref(schRef);
          schedulerRef.current = schRef;
        }}
        {...props}
        {...SCHEDULER_CONFIG}
        onMouseOver={() => {
          // See SwivuHeaderBottomTier.jsx tickSize change handler for more info
          currentViewRef.current.swivuLastViewportCenterDate = schedulerRef.current.instance.viewportCenterDate;
        }}
        disabled={user && !user.can(GRANTS.grant_four)}
        readOnly={user && !user.can(GRANTS.grant_four)}
        onEventDblClick={({eventRecord}) => {
          const taskRecord = getOriginalRecord(eventRecord.task);
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id });
        }}
        onCellClick={onGridCellClick}
        onCellDblClick={showResourcePopupEditor}
        onEventMenuItem={(event) => {
          if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.newTask) {
            if(event.item.up("menuitem").ref == SwivuSchedulerMenuPlugin.itemsKeyRef.insertTask){
              onCreateNewTask(event);
            }else {
              onCreateNewTaskReplaceEvent(event);
            }
          } else if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.addReminderOnEvent) {
            addReminderOnTask(event);
          } else if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.findTask) {
            onTaskFind(event);
          } else if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.taskFiles) {
            const taskRecord = getOriginalRecord(event.eventRecord.task);
            editorHandler.openEditor(EDITOR_KEYS.taskFilesEditor, taskRecord, { task: taskRecord });
          } else if (event.item.ref === "adjustPoints") {
            openNewSpotIncentiveEditor({ project, editorHandler, otherParams: { selectedTask: event.eventRecord.task } });
          }
        }}
        onScheduleMenuItem={(event) => {
          if (event.item.ref === SwivuSchedulerMenuPlugin.itemsKeyRef.newTask) onCreateNewTask(event);
          else if (event.item.ref === SwivuSchedulerMenuPlugin.itemsKeyRef.findTask) onTaskFind(event);
        }}
        onBeforeDragCreateFinalize={onCreateNewTaskEventByDragCreate}
        stateId={props.stateId}
        plugins={props.plugins}
        onHeaderMenuItem={({source, item}) => {
          if (item._menuId === "resource") {
            const newRecord = source.store.createRecord();
            editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, newRecord, {resource: newRecord});
          }
        }}
        onPaste={onSwivuEventPaste}
        onBeforePaste={onSwivuBeforePaste}
      />
    </>
  );
});

export default BryntumSchedulerWrapper;
